import { action, computed, makeObservable, observable } from 'mobx';
import moment from 'moment';
import client from 'src/services/client';
import {
  IMenuSet,
  IT360IntegrationsErrors,
  IT360IntegrationSettings,
  IT360IntegrationsSettings,
  IT360IntegrationTab,
  ITakerAndToYouBranches,
  TakerToYouBranchesMapping,
} from 'src/types/integrations';
import { LoadingStatus } from 'src/types/main';
import { takerToYouBranchesDTO } from '~/dto/TakerToYouBranchesDTO';
import { getAbortController, isCanceledRequest } from '~/helpers';
import { appStore } from '~/mobx/appStore';
import { localeStore } from '~/mobx/localesStore';
import logger from '~/services/logger';
import { toastSuccess } from '~/utils/toasts';
import { IPickerOption } from '~/view/components/Controls/Pickers/Picker/types';
import { timeConfirmStore } from '~/view/containers/Modals/TimeConfirmModal/TimeConfirmStore';
import { branchStore } from '~/mobx/branchStore';

export class T360IntegrationsStore {
  constructor() {
    makeObservable(this);
  }

  private getIntegrationAbortController = new AbortController();
  private updateIntegrationAbortController = new AbortController();
  private syncIntegrationAbortController = new AbortController();
  private getTakerAndToYouBranchesAbortController = new AbortController();
  private setTakerAndToYouBranchesAbortController = new AbortController();

  @observable public integrationLoadingStatus: LoadingStatus = LoadingStatus.Init;
  @observable public takerBranchesLoadingStatus: LoadingStatus = LoadingStatus.Init;
  @observable public takerAndToYouBranchesLoadingStatus: LoadingStatus = LoadingStatus.Init;

  @observable public errors: IT360IntegrationsErrors = {};
  @observable public integrationsSettings: IT360IntegrationsSettings<IT360IntegrationSettings> | null = null;
  @observable public integrationsSettingsTemp: { [key: number]: IT360IntegrationSettings } = {};
  @observable public activeTabName: string | undefined = undefined;
  @observable public takerAndToYouBranches: ITakerAndToYouBranches | null = null;
  @observable public isHasChangesInTakerAndToYouBranches: boolean = false;

  @action public getSettings = async (refresh: boolean = false) => {
    this.getIntegrationAbortController.abort();
    this.getIntegrationAbortController = getAbortController();
    this.errors = {};

    try {
      this.integrationLoadingStatus = refresh ? LoadingStatus.Refreshing : LoadingStatus.Loading;

      this.integrationsSettings = await client.get('v3/integrations/t360', { signal: this.getIntegrationAbortController.signal });

      const newTemp: { [key: number]: IT360IntegrationSettings } = {};
      this.integrationsSettings?.integrations.forEach((integrationSettings) => {
        integrationSettings.settings.timeslots = integrationSettings.settings.timeslots.map((timeslot: any): any => ({
          start_time: moment(timeslot.start_time, 'HH:mm').format('HH:mm:ss'),
          close_time: moment(timeslot.close_time, 'HH:mm').format('HH:mm:ss'),
          weekday: timeslot.weekday,
        }));
        newTemp[integrationSettings.id] = integrationSettings;
      });

      this.integrationsSettingsTemp = newTemp;

      if (this.activeTabName === undefined) {
        this.activeTabName = this.integrationsSettings?.integrations[0].name;
      }

      this.integrationLoadingStatus = LoadingStatus.Success;
    } catch (error: any) {
      if (isCanceledRequest(error)) {
        this.integrationLoadingStatus = LoadingStatus.Success;
        return;
      }

      this.integrationLoadingStatus = LoadingStatus.Error;
      logger.error({ error, place: 't360IntegrationStore getSettings', showModal: true });
    }
  };

  @action public updateSettings = async (integrationId: number) => {
    this.updateIntegrationAbortController.abort();
    this.updateIntegrationAbortController = getAbortController();
    this.errors = {};
    try {
      this.integrationLoadingStatus = LoadingStatus.Refreshing;

      await client.put(
        // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
        `v3/integrations/t360/${integrationId}`,
        { ...this.integrationsSettingsTemp[integrationId] },
        { signal: this.updateIntegrationAbortController.signal }
      );
      this.integrationLoadingStatus = LoadingStatus.Success;
      this.getSettings(true);
    } catch (error: any) {
      if (isCanceledRequest(error)) {
        this.integrationLoadingStatus = LoadingStatus.Success;
        return;
      }
      let showModal = true;
      if (error.hasOwnProperty('response') && error.response.hasOwnProperty('data') && error.response.data.hasOwnProperty('errors')) {
        this.errors[integrationId] = error.response.data.errors;
        showModal = false;
      }
      this.integrationLoadingStatus = LoadingStatus.Error;
      logger.error({ error, place: 't360IntegrationStore setSettings', showModal });
    }
  };

  @action public runSync = async (integrationId: number) => {
    this.syncIntegrationAbortController.abort();
    this.syncIntegrationAbortController = getAbortController();
    this.errors = {};
    try {
      this.integrationLoadingStatus = LoadingStatus.Refreshing;

      await client.post(`v3/integrations/t360/${integrationId}/sync`, {}, { signal: this.syncIntegrationAbortController.signal });
      await appStore.fetchSettings(false);
      this.integrationLoadingStatus = LoadingStatus.Success;
      this.getSettings(true);
    } catch (error: any) {
      if (isCanceledRequest(error)) {
        this.integrationLoadingStatus = LoadingStatus.Success;
        return;
      }
      this.integrationLoadingStatus = LoadingStatus.Error;
      logger.error({ error, place: 't360IntegrationStore setSettings', showModal: true });
    }
  };
  @action public loadAllTakerBranches = async () => {
    this.integrationLoadingStatus = LoadingStatus.Refreshing;
    await branchStore.fetchAllBranches(true, {
      sort_field: 'name',
      is_delete: 0,
    });
    this.integrationLoadingStatus = LoadingStatus.Success;
  };

  @action public fetchTakerAndToYouBranches = async (): Promise<void> => {
    if (this.getTakerAndToYouBranchesAbortController) {
      this.getTakerAndToYouBranchesAbortController.abort();
    }

    this.getTakerAndToYouBranchesAbortController = getAbortController();
    this.errors = {};

    try {
      this.takerAndToYouBranchesLoadingStatus = LoadingStatus.Refreshing;
      this.isHasChangesInTakerAndToYouBranches = false;

      const takerAndToYouBranches = await client.get(`v3/integrations/t360/toyou/branches`, {
        signal: this.getTakerAndToYouBranchesAbortController.signal,
      });
      this.takerAndToYouBranches = takerToYouBranchesDTO(takerAndToYouBranches);

      this.takerAndToYouBranchesLoadingStatus = LoadingStatus.Success;
    } catch (error: any) {
      if (isCanceledRequest(error)) {
        this.takerAndToYouBranchesLoadingStatus = LoadingStatus.Success;
        return;
      }

      this.takerAndToYouBranchesLoadingStatus = LoadingStatus.Error;
      timeConfirmStore.handleClose();
      logger.error({ error, place: 't360IntegrationStore fetchTakerAndToYouBranches', showModal: true });
    }
  };

  @action public updateBranchMapping = (takerBranchId: number, toYouBranchId: string | null) => {
    if (!this.takerAndToYouBranches) return;

    this.takerAndToYouBranches = {
      ...this.takerAndToYouBranches,
      to_you_branches: this.takerAndToYouBranches.to_you_branches.map((branch) => {
        if (branch.taker_branch_id === takerBranchId) {
          branch = {
            ...branch,
            taker_branch_id: null,
            is_new_select: false,
          };

          this.isHasChangesInTakerAndToYouBranches = true;
        }
        if (branch.id === toYouBranchId) {
          branch = {
            ...branch,
            taker_branch_id: takerBranchId,
            is_new_select: true,
          };

          this.isHasChangesInTakerAndToYouBranches = true;
        }
        return branch;
      }),
    };
  };

  @action public matchTakerAndToYouBranches = async (): Promise<void> => {
    if (this.setTakerAndToYouBranchesAbortController) {
      this.setTakerAndToYouBranchesAbortController.abort();
    }

    this.setTakerAndToYouBranchesAbortController = getAbortController();
    this.errors = {};

    try {
      // this.integrationLoadingStatus = LoadingStatus.Refreshing;

      if (!this.takerAndToYouBranches) return;

      const mapping: TakerToYouBranchesMapping = {};
      this.takerAndToYouBranches.to_you_branches.forEach((branch) => {
        if (branch.taker_branch_id) {
          mapping[branch.taker_branch_id] = branch.id;
        }
      });

      await client.post(
        `v3/integrations/t360/toyou/branches`,
        { mapping },
        {
          signal: this.setTakerAndToYouBranchesAbortController.signal,
        }
      );

      this.integrationLoadingStatus = LoadingStatus.Success;

      toastSuccess({
        message: localeStore.t('toast_branch_matching_changes_saved'),
      });
    } catch (error: any) {
      if (isCanceledRequest(error)) {
        this.integrationLoadingStatus = LoadingStatus.Success;
        return;
      }

      this.integrationLoadingStatus = LoadingStatus.Error;
      logger.error({ error, place: 't360IntegrationStore matchTakerAndToYouBranches', showModal: true });
    }
  };

  @action public updateIntegrationSettingsTemp = (integrationId: number, settings: IT360IntegrationSettings) => {
    const newSettings = JSON.parse(JSON.stringify(this.integrationsSettingsTemp));
    newSettings[integrationId] = settings;
    this.integrationsSettingsTemp = newSettings;
  };

  @action public setActiveTab = (tabName: string) => {
    this.activeTabName = tabName;
  };

  @action public getIntegrationSettings = (integrationId: number): IT360IntegrationSettings | null => {
    return this.integrationsSettings?.integrations.find((integration) => integration.id === integrationId) ?? null;
  };

  @computed get isValidCredentials(): (integrationId: number) => boolean {
    return (integrationId: number): boolean => {
      const settings = t360IntegrationStore.getIntegrationSettings(integrationId)?.settings;

      return !(settings?.email?.trim() && settings?.password?.trim());
    };
  }

  @computed get isLoading(): boolean {
    return [LoadingStatus.Init, LoadingStatus.Loading].includes(this.integrationLoadingStatus);
  }

  @computed get isRefreshing(): boolean {
    return [LoadingStatus.Refreshing].includes(this.integrationLoadingStatus);
  }

  @computed get isOnLoading(): boolean {
    return [LoadingStatus.Refreshing, LoadingStatus.Loading].includes(this.integrationLoadingStatus);
  }

  @computed get menuGroupsOptions(): IPickerOption[] {
    return (
      this.integrationsSettings?.menu_sets.map(
        (menuSet: IMenuSet): IPickerOption => ({
          label: menuSet.category,
          value: menuSet.id,
        })
      ) ?? []
    );
  }

  @computed get tabs(): IT360IntegrationTab[] {
    return (
      this.integrationsSettings?.integrations.map((integration: IT360IntegrationSettings) => ({
        id: integration.id,
        name: integration.name,
        provider: integration.provider,
      })) ?? []
    );
  }

  @action public unMount = () => {
    this.getIntegrationAbortController.abort();
    this.updateIntegrationAbortController.abort();
    this.syncIntegrationAbortController.abort();

    this.integrationLoadingStatus = LoadingStatus.Init;
    this.takerBranchesLoadingStatus = LoadingStatus.Init;

    this.integrationsSettings = null;
    this.integrationsSettingsTemp = {};

    this.errors = {};
  };
}

const t360IntegrationStore = new T360IntegrationsStore();
export default t360IntegrationStore;
